import type { Todo } from "@ronin/contracts";
import { isOverdue } from "@ronin/core";
import { Checkbox, cn } from "@ronin/ui";
import { Calendar, Zap } from "lucide-react";
import { formatDue } from "../../lib/due.js";
import { useTimeZone } from "../settings/use-settings.js";

interface TodoItemProps {
  todo: Todo;
  onOpen: (todo: Todo) => void;
  onToggleDone: (todo: Todo) => void;
  onToggleImportant: (todo: Todo) => void;
}

/**
 * One row with a fixed height, so every todo is the same size whether or not it has a
 * description or due date. The title button covers the whole row (opens the dialog); the
 * checkbox and lightning sit above it.
 */
export function TodoItem({ todo, onOpen, onToggleDone, onToggleImportant }: TodoItemProps) {
  const timeZone = useTimeZone();
  const overdue = isOverdue(todo, new Date(), timeZone);
  const description = todo.description?.split("\n")[0];
  const hasMeta = Boolean(description) || Boolean(todo.dueDate);

  return (
    <li
      className={cn(
        "group relative flex h-16 items-center gap-3.5 border bg-card px-4 transition-colors duration-150 hover:bg-muted/40",
        todo.done && "bg-card/60",
      )}
    >
      <Checkbox
        className="relative z-10"
        checked={todo.done}
        aria-label={todo.done ? `Mark "${todo.title}" as not done` : `Mark "${todo.title}" as done`}
        onCheckedChange={() => onToggleDone(todo)}
      />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        <button
          type="button"
          onClick={() => onOpen(todo)}
          className="truncate text-left text-sm/5 font-medium text-foreground outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring data-[done=true]:text-faint-foreground data-[done=true]:line-through"
          data-done={todo.done}
        >
          {todo.title}
        </button>
        {hasMeta && (
          <div
            // On desktop the due chip sits on the right, so without a description this line is empty.
            className={cn("flex h-[18px] min-w-0 items-center gap-2", !description && "sm:hidden")}
          >
            <DueChip todo={todo} overdue={overdue} className="sm:hidden" />
            {description && (
              <p className="truncate text-xs/[18px] text-muted-foreground">{description}</p>
            )}
          </div>
        )}
      </div>

      <DueChip todo={todo} overdue={overdue} className="hidden sm:inline-flex" />

      <button
        type="button"
        aria-label={
          todo.important ? `Mark "${todo.title}" as normal` : `Mark "${todo.title}" as important`
        }
        aria-pressed={todo.important}
        title={todo.important ? "Unmark as important" : "Mark as important"}
        onClick={() => onToggleImportant(todo)}
        className={cn(
          "touch-target relative z-10 shrink-0 p-1.5 transition-[color,background-color,opacity] duration-150 focus-visible:outline-2 focus-visible:outline-ring",
          todo.important
            ? "text-important hover:bg-important/10"
            : "text-dim-foreground opacity-60 hover:bg-muted hover:text-secondary-foreground group-hover:opacity-100 pointer-coarse:opacity-100",
        )}
      >
        <Zap
          className="size-4"
          fill={todo.important ? "currentColor" : "none"}
          fillOpacity={0.25}
        />
      </button>
    </li>
  );
}

function DueChip({
  todo,
  overdue,
  className,
}: {
  todo: Todo;
  overdue: boolean;
  className?: string;
}) {
  const timeZone = useTimeZone();
  if (!todo.dueDate) return null;
  return (
    <span
      className={cn(
        "relative inline-flex h-[22px] shrink-0 items-center gap-1 px-2 text-xs font-medium max-sm:h-[18px] max-sm:px-1.5",
        overdue ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground",
        className,
      )}
    >
      <Calendar className="size-3" aria-hidden />
      {overdue && "Overdue · "}
      {formatDue(todo.dueDate, todo.dueTime, new Date(), timeZone)}
    </span>
  );
}
